import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** `split`: copy + visual; `cover`: copy with room for the torus; `wide`: one column. */
export type SlideLayoutVariant = 'split' | 'cover' | 'wide';

const COLUMNS: Record<SlideLayoutVariant, string> = {
  split: 'md:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]',
  // The right side stays empty: that's where the torus sits (NEWS-10).
  cover: 'md:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]',
  wide: 'md:grid-cols-[minmax(0,1fr)]',
};

/**
 * A slide's frame: copy and visual side by side from 768px, stacked below
 * (visual on top, or none on the cover/outro, where the torus is). Below
 * 768px it scrolls inside when it doesn't fit, never sideways (NEWS-14).
 */
export function SlideLayout({
  variant = 'split',
  copy,
  visual,
}: {
  variant?: SlideLayoutVariant;
  copy: ReactNode;
  visual?: ReactNode;
}) {
  const stacksVisual = variant === 'split' && visual !== undefined;
  return (
    <div
      data-testid="slide-layout"
      className={cn(
        'absolute inset-0 grid items-center gap-10 py-2 pl-12 pr-11',
        COLUMNS[variant],
        'max-md:grid-cols-[minmax(0,1fr)] max-md:content-start max-md:gap-5 max-md:overflow-y-auto max-md:overflow-x-hidden max-md:py-3 max-md:pl-[max(18px,env(safe-area-inset-left))] max-md:pr-[max(18px,env(safe-area-inset-right))]',
        // < 768px the torus fills the top band, down to ~55% of the screen
        // (NEWS-10): the copy starts below it (the band minus the player's
        // header), sits at the bottom, and scrolls when longer. A column with
        // an auto margin, not `align-content: end`, which pushes overflowing
        // copy past the top where it can't be scrolled to (NEWS-14).
        variant === 'cover' &&
          'max-md:flex max-md:flex-col max-md:pb-5 max-md:pt-[calc(55dvh-72px)]'
      )}
    >
      <div
        className={cn(
          'relative flex min-w-0 flex-col gap-[18px] [@media(min-width:768px)_and_(max-height:800px)]:gap-3.5',
          variant === 'cover' && 'max-md:mt-auto'
        )}
      >
        {copy}
      </div>
      {variant !== 'wide' && (
        <div
          className={cn(
            'relative min-w-0',
            stacksVisual ? 'max-md:order-first' : 'max-md:hidden'
          )}
        >
          {visual}
        </div>
      )}
    </div>
  );
}

/**
 * The slide's title: 46px (cover/outro `xl` 56px), 38/46px on short screens
 * and 30/36px below 768px (NEWS-14). Put a `WordReveal` inside.
 */
export function SlideTitle({
  size = 'default',
  children,
}: {
  size?: 'default' | 'xl';
  children: ReactNode;
}) {
  return (
    <h2
      className={cn(
        'm-0 font-medium tracking-[-0.035em] text-balance',
        size === 'xl'
          ? // Short laptop screens (≥ 768px, ≤ 800px tall): the smaller scale.
            'text-[56px] leading-[1.02] max-md:text-[36px] [@media(min-width:768px)_and_(max-height:800px)]:text-[46px]'
          : 'text-[46px] leading-[1.06] max-md:text-[30px] [@media(min-width:768px)_and_(max-height:800px)]:text-[38px]'
      )}
    >
      {children}
    </h2>
  );
}

/** The lead's text box (also what a `TextReserve` of a lead measures with). */
export const SLIDE_LEAD =
  'm-0 max-w-[440px] text-base leading-[1.55] text-white/[.86] max-md:text-[14.5px] [&_b]:font-semibold [&_b]:text-white [@media(min-width:768px)_and_(max-height:800px)]:text-[15px]';

/** The slide's text under the title. */
export function SlideLead({ children }: { children: ReactNode }) {
  return <p className={SLIDE_LEAD}>{children}</p>;
}
